/**
 * Centralized error code registry for all Beacon SDK errors.
 * Error codes provide unique identifiers for debugging and support.
 */

/**
 * WalletConnect transport error codes
 * Reference: @walletconnect/utils SDK_ERRORS and INTERNAL_ERRORS
 */
export const WC_ERROR_CODES = {
  // Custom Beacon SDK WC errors
  INVALID_NAMESPACE: 'WC_INVALID_NAMESPACE',
  INCOMPLETE_NAMESPACE: 'WC_INCOMPLETE_NAMESPACE',
  NOT_CONNECTED: 'WC_NOT_CONNECTED',
  INVALID_SESSION: 'WC_INVALID_SESSION',
  MISSING_REQUIRED_SCOPE: 'WC_MISSING_REQUIRED_SCOPE',
  ACTIVE_NETWORK_UNSPECIFIED: 'WC_ACTIVE_NETWORK_UNSPECIFIED',
  ACTIVE_ACCOUNT_UNSPECIFIED: 'WC_ACTIVE_ACCOUNT_UNSPECIFIED',
  INVALID_NETWORK_OR_ACCOUNT: 'WC_INVALID_NETWORK_OR_ACCOUNT',

  // WC INTERNAL_ERRORS (from @walletconnect/utils)
  NOT_INITIALIZED: 'WC_NOT_INITIALIZED', // code 1
  NO_MATCHING_KEY: 'WC_NO_MATCHING_KEY', // code 2
  EXPIRED: 'WC_EXPIRED', // code 6 (session or pairing expiry)
  SESSION_EXPIRED: 'WC_SESSION_EXPIRED',
  PAIRING_EXPIRED: 'WC_PAIRING_EXPIRED',
  UNKNOWN_TYPE: 'WC_UNKNOWN_TYPE', // code 7
  MISMATCHED_TOPIC: 'WC_MISMATCHED_TOPIC', // code 8
  NON_CONFORMING_NAMESPACES: 'WC_NON_CONFORMING_NAMESPACES', // code 9

  // WC SDK_ERRORS (from @walletconnect/utils)
  INVALID_METHOD: 'WC_INVALID_METHOD', // code 1001
  INVALID_EVENT: 'WC_INVALID_EVENT', // code 1002
  INVALID_UPDATE_REQUEST: 'WC_INVALID_UPDATE_REQUEST', // code 1003
  INVALID_EXTEND_REQUEST: 'WC_INVALID_EXTEND_REQUEST', // code 1004
  INVALID_SESSION_SETTLE_REQUEST: 'WC_INVALID_SESSION_SETTLE_REQUEST', // code 1005
  UNAUTHORIZED_METHOD: 'WC_UNAUTHORIZED_METHOD', // code 3001
  UNAUTHORIZED_EVENT: 'WC_UNAUTHORIZED_EVENT', // code 3002
  UNAUTHORIZED_UPDATE_REQUEST: 'WC_UNAUTHORIZED_UPDATE_REQUEST', // code 3003
  UNAUTHORIZED_EXTEND_REQUEST: 'WC_UNAUTHORIZED_EXTEND_REQUEST', // code 3004
  USER_REJECTED: 'WC_USER_REJECTED', // code 5000
  USER_REJECTED_CHAINS: 'WC_USER_REJECTED_CHAINS', // code 5001
  USER_REJECTED_METHODS: 'WC_USER_REJECTED_METHODS', // code 5002
  USER_REJECTED_EVENTS: 'WC_USER_REJECTED_EVENTS', // code 5003
  UNSUPPORTED_CHAINS: 'WC_UNSUPPORTED_CHAINS', // code 5100
  UNSUPPORTED_METHODS: 'WC_UNSUPPORTED_METHODS', // code 5101
  UNSUPPORTED_EVENTS: 'WC_UNSUPPORTED_EVENTS', // code 5102
  UNSUPPORTED_ACCOUNTS: 'WC_UNSUPPORTED_ACCOUNTS', // code 5103
  UNSUPPORTED_NAMESPACE_KEY: 'WC_UNSUPPORTED_NAMESPACE_KEY', // code 5104
  USER_DISCONNECTED: 'WC_USER_DISCONNECTED', // code 6000
  SESSION_SETTLEMENT_FAILED: 'WC_SESSION_SETTLEMENT_FAILED', // code 7000
  WC_METHOD_UNSUPPORTED: 'WC_METHOD_UNSUPPORTED', // code 10001

  // Generic
  UNKNOWN_ERROR: 'WC_UNKNOWN_ERROR'
} as const

/**
 * Matrix/P2P transport error codes
 */
export const MATRIX_ERROR_CODES = {
  RELAY_TIMEOUT: 'MATRIX_RELAY_TIMEOUT',
  CONNECTION_FAILED: 'MATRIX_CONNECTION_FAILED',
  RELAY_ERROR: 'MATRIX_RELAY_ERROR',
  PEER_NOT_FOUND: 'MATRIX_PEER_NOT_FOUND',
  MESSAGE_DELIVERY_FAILED: 'MATRIX_MESSAGE_DELIVERY_FAILED',
  ROOM_JOIN_FAILED: 'MATRIX_ROOM_JOIN_FAILED'
} as const

/**
 * PostMessage transport error codes
 */
export const PM_ERROR_CODES = {
  EXTENSION_ERROR: 'PM_EXTENSION_ERROR',
  ORIGIN_MISMATCH: 'PM_ORIGIN_MISMATCH',
  NO_RESPONSE: 'PM_NO_RESPONSE',
  EXTENSION_NOT_FOUND: 'PM_EXTENSION_NOT_FOUND',
  MESSAGE_TIMEOUT: 'PM_MESSAGE_TIMEOUT'
} as const

/**
 * Blockchain-specific error codes
 */
export const BLOCKCHAIN_ERROR_CODES = {
  TEZOS_INVALID_SIGNATURE: 'TEZOS_INVALID_SIGNATURE',
  TEZOS_NETWORK_ERROR: 'TEZOS_NETWORK_ERROR',
  TEZOS_NODE_ERROR: 'TEZOS_NODE_ERROR',
  TEZOS_SIMULATION_ERROR: 'TEZOS_SIMULATION_ERROR',
  SUBSTRATE_INVALID_SIGNATURE: 'SUBSTRATE_INVALID_SIGNATURE',
  SUBSTRATE_NETWORK_ERROR: 'SUBSTRATE_NETWORK_ERROR'
} as const

/**
 * Generic Beacon error codes
 */
export const BEACON_ERROR_CODES = {
  ABORTED_BY_USER: 'ABORTED_BY_USER',
  NETWORK_NOT_SUPPORTED: 'NETWORK_NOT_SUPPORTED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  NO_ADDRESS: 'NO_ADDRESS',
  NO_PRIVATE_KEY: 'NO_PRIVATE_KEY',
  PARAMETERS_INVALID: 'PARAMETERS_INVALID',
  TOO_MANY_OPERATIONS: 'TOO_MANY_OPERATIONS',
  TRANSACTION_INVALID: 'TRANSACTION_INVALID',
  BROADCAST_ERROR: 'BROADCAST_ERROR',
  SIGNATURE_TYPE_NOT_SUPPORTED: 'SIGNATURE_TYPE_NOT_SUPPORTED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  VERSION_UNSUPPORTED: 'VERSION_UNSUPPORTED',
  INVALID_REQUIRED_MINIMUM_VERSION: 'INVALID_REQUIRED_MINIMUM_VERSION',
  NETWORKS_UNSUPPORTED: 'NETWORKS_UNSUPPORTED',
  INVALID_BEACON_VERSION: 'INVALID_BEACON_VERSION'
} as const

/**
 * All error codes combined
 */
export const ERROR_CODES = {
  ...WC_ERROR_CODES,
  ...MATRIX_ERROR_CODES,
  ...PM_ERROR_CODES,
  ...BLOCKCHAIN_ERROR_CODES,
  ...BEACON_ERROR_CODES
} as const

/**
 * Type for all possible error codes
 */
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES]
